import type { BenefitSummary } from "@yutai/shared"
import { Link } from "react-router-dom"
import { BenefitSummaryStats } from "../../components/BenefitSummaryStats.js"
import { BenefitTiersTable } from "../../components/BenefitTiersTable.js"
import { CategoryBadge } from "../../components/CategoryBadge.js"
import { HighlightMatch } from "../../components/HighlightMatch.js"
import { RecordDeadlineRibbon } from "../../components/RecordDeadlineRibbon.js"
import { TagBadge } from "../../components/TagBadge.js"
import { googleSearchUrl, yahooFinanceJpUrl } from "../../lib/financeLinks.js"
import textLinkStyles from "../../styles/textLink.module.css"
import { FavoriteButton } from "../lists/FavoriteButton.js"
import styles from "./BenefitCard.module.css"

interface Props {
  benefit: BenefitSummary
  query?: string
  borderVariant?: "default" | "gradient"
}

export function BenefitCard({ benefit, query = "", borderVariant = "default" }: Props) {
  return (
    <article
      className={`${styles.card} ${borderVariant === "gradient" ? styles.cardGradientBorder : ""}`}
    >
      <RecordDeadlineRibbon benefit={benefit} />
      <div className={styles.companyInfo}>
        <div className={styles.companyMeta}>
          <a
            href={yahooFinanceJpUrl(benefit.id)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.codeLink}
            title="Yahoo!ファイナンスで株価を見る"
          >
            {benefit.id}
          </a>
          <span className={styles.industry}>| {benefit.industry}</span>
        </div>
        <h2 className={styles.name}>
          <Link to={`/companies/${benefit.id}`}>{benefit.name}</Link>
        </h2>
        {benefit.businessDescription && (
          <p className={styles.businessDescription}>
            <HighlightMatch text={benefit.businessDescription} query={query} />
          </p>
        )}
      </div>

      {(benefit.categories.length > 0 || benefit.tags.length > 0) && (
        <div className={styles.badges}>
          {benefit.categories.map((cat) => (
            <CategoryBadge key={cat} category={cat} />
          ))}
          {benefit.tags.map((tag) => (
            <TagBadge key={tag} tag={tag} />
          ))}
        </div>
      )}

      <div className={styles.benefitSection}>
        {benefit.description && (
          <p className={styles.description}>
            <HighlightMatch text={benefit.description} query={query} />
          </p>
        )}

        <BenefitTiersTable tiers={benefit.tiers} query={query} />

        <BenefitSummaryStats benefit={benefit} />

        <div className={styles.links}>
          <a
            href={benefit.officialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={textLinkStyles.link}
          >
            公式優待ページ ↗
          </a>
          <a
            href={yahooFinanceJpUrl(benefit.id)}
            target="_blank"
            rel="noopener noreferrer"
            className={textLinkStyles.link}
          >
            Yahoo!ファイナンス ↗
          </a>
          <a
            href={googleSearchUrl(benefit.name)}
            target="_blank"
            rel="noopener noreferrer"
            className={textLinkStyles.link}
          >
            優待ブログ検索 ↗
          </a>
          <div className={styles.favoriteSlot}>
            <FavoriteButton companyId={benefit.id} popoverPlacement="above" />
          </div>
        </div>
      </div>
    </article>
  )
}
